// Iter 82.2 / Phase E1.2 — Leave Request Apply form.
// Employee-facing leave application: leave type, dates, half-day flags,
// reason. Live conflict + duration + balance preview. Submit goes to
// Pending; Save Draft stores without triggering workflow.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, FloppyDisk, PaperPlaneTilt, Warning, Info, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const HALF_DAY_OPTIONS = [
    { value: "full", label: "Full day" },
    { value: "first_half", label: "First half" },
    { value: "second_half", label: "Second half" },
];

export default function LeaveRequestApply() {
    const navigate = useNavigate();
    const { user, can } = useAuth();
    const [me, setMe] = useState(null);
    const [policies, setPolicies] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [employeeId, setEmployeeId] = useState("");
    const [form, setForm] = useState({
        leave_type_code: "",
        from_date: "",
        to_date: "",
        first_half: "full",
        last_half: "full",
        reason: "",
    });
    const [duration, setDuration] = useState(null);
    const [conflicts, setConflicts] = useState(null);
    const [balance, setBalance] = useState(null);
    // P0-A Batch 2 · provisional Paid/LOP split preview (advisory, not
    // authoritative). Populated when the resolved policy allows
    // LOP-splitting; the authoritative split is computed at payroll
    // freeze via routes_leave_reconciliation.py.
    const [splitPreview, setSplitPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Iter 106.0 · surface silent /hr/ess/me failures so an unlinked
    // user account does not present an empty Leave Type dropdown with
    // no explanation. Distinct states help us tailor the banner:
    //   - "unlinked"       → 404 (account has no employee record)
    //   - "error"          → other failure (network / auth / 5xx)
    //   - "policies-empty" → me resolved but no policies came back
    const [meLoadState, setMeLoadState] = useState("loading");
    const [policiesLoadState, setPoliciesLoadState] = useState("idle");

    // Iter 105.0 · anchor HR flag on employee:admin permission (aligns
    // with backend `_is_hr_user` helper). Prior gate used
    // `leave:admin || super_admin` which excluded hr_user role.
    const isHr = can("employee:admin");

    const reloadMe = useCallback(async () => {
        setMeLoadState("loading");
        try {
            const { data } = await api.get("/hr/ess/me");
            setMe(data);
            setEmployeeId(data?.id || "");
            setMeLoadState("ok");
        } catch (err) {
            setMe(null);
            const status = err?.response?.status;
            if (status === 404) {
                setMeLoadState("unlinked");
                toast.error(
                    "Your account is not linked to an employee record. Ask HR to complete the linkage before applying leave.",
                );
            } else {
                setMeLoadState("error");
                toast.error(formatApiError(err, "Unable to load your employee profile"));
            }
        }
    }, []);

    const reloadEmployees = useCallback(async () => {
        if (!isHr) return;
        try {
            const { data } = await api.get("/hr/employee/list", { params: { page_size: 200, status: "Active" } });
            setEmployees(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load employee list"));
        }
    }, [isHr]);

    const reloadPolicies = useCallback(async (eid) => {
        if (!eid) {
            setPolicies([]);
            setPoliciesLoadState("idle");
            return;
        }
        setPoliciesLoadState("loading");
        try {
            const { data } = await api.get("/leave/policy/resolve", { params: { employee_id: eid } });
            const winners = data.winners || [];
            setPolicies(winners);
            setPoliciesLoadState(winners.length ? "ok" : "empty");
            if (!winners.length) {
                toast.error(
                    "No leave policies are applicable to your profile yet. Ask HR to assign a leave policy.",
                );
            }
        } catch (err) {
            setPolicies([]);
            setPoliciesLoadState("error");
            toast.error(formatApiError(err, "Unable to load leave policies"));
        }
    }, []);

    useEffect(() => { Promise.resolve().then(reloadMe); }, [reloadMe]);
    useEffect(() => { Promise.resolve().then(reloadEmployees); }, [reloadEmployees]);
    useEffect(() => { if (employeeId) reloadPolicies(employeeId); }, [employeeId, reloadPolicies]);

    // Live duration preview
    useEffect(() => {
        if (!employeeId || !form.leave_type_code || !form.from_date || !form.to_date) {
            setDuration(null);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/leave/request/duration-preview", {
                    employee_id: employeeId,
                    leave_type_code: form.leave_type_code,
                    from_date: form.from_date,
                    to_date: form.to_date,
                    first_half: form.first_half,
                    last_half: form.last_half,
                });
                setDuration(data);
            } catch (err) {
                setDuration(null);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [employeeId, form.leave_type_code, form.from_date, form.to_date, form.first_half, form.last_half]);

    // Live conflict check
    useEffect(() => {
        if (!employeeId || !form.from_date || !form.to_date) {
            setConflicts(null);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/leave/request/conflicts", {
                    params: {
                        employee_id: employeeId,
                        from_date: form.from_date,
                        to_date: form.to_date,
                    },
                });
                setConflicts(data);
            } catch {
                setConflicts(null);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [employeeId, form.from_date, form.to_date]);

    // Balance lookup
    useEffect(() => {
        if (!employeeId || !form.leave_type_code) { setBalance(null); return; }
        api.get(`/leave/balance/${employeeId}/${form.leave_type_code}`)
            .then(({ data }) => setBalance(data.balance))
            .catch(() => setBalance(null));
    }, [employeeId, form.leave_type_code]);

    // P0-A Batch 2 · provisional split preview. Fires only when we
    // have (employee × leave type × dates); backend returns
    // { policy_allows_split, provisional_paid_days, provisional_lop_days,
    //   ledger_balance, reserved_days, available_balance, note }.
    useEffect(() => {
        if (!employeeId || !form.leave_type_code || !form.from_date || !form.to_date) {
            setSplitPreview(null);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/leave/request/provisional-split-preview", {
                    employee_id: employeeId,
                    leave_type_code: form.leave_type_code,
                    from_date: form.from_date,
                    to_date: form.to_date,
                    first_half: form.first_half,
                    last_half: form.last_half,
                });
                setSplitPreview(data);
            } catch {
                setSplitPreview(null);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [employeeId, form.leave_type_code, form.from_date, form.to_date, form.first_half, form.last_half]);

    const selectedPolicy = useMemo(
        () => policies.find((p) => p.code === form.leave_type_code),
        [policies, form.leave_type_code],
    );

    const canSubmit = (
        employeeId && form.leave_type_code && form.from_date && form.to_date
        && form.reason?.trim().length >= 3
        && duration && duration.days_total > 0
        && !(conflicts?.has_conflicts)
    );

    const balanceShortfall = (
        selectedPolicy && !selectedPolicy.is_lop && duration && balance
        && (balance.current_balance ?? 0) < duration.days_total
    );

    const submit = async (submitNow) => {
        if (balanceShortfall && submitNow) {
            toast.error("Insufficient balance — save as Draft or pick a different leave type");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...(isHr ? { employee_id: employeeId } : {}),
                leave_type_code: form.leave_type_code,
                from_date: form.from_date,
                to_date: form.to_date,
                first_half: form.first_half,
                last_half: form.last_half,
                reason: form.reason.trim(),
                submit: submitNow,
            };
            const { data } = await api.post("/leave/request", payload);
            toast.success(submitNow ? "Request submitted for approval" : "Draft saved");
            navigate(`/leave/requests/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1300px]" data-testid="leave-apply-page">
            <PageHeader
                eyebrow="Leave Operations"
                title="Apply for Leave"
                description="Save as Draft to refine later, or Submit directly to start the approval workflow. Server-side validation respects your policy, holiday calendar and weekends."
            />

            {/* Iter 106.0 · surface silent failures with a business-
                friendly banner. `meLoadState` covers the /hr/ess/me
                resolution (account → employee linkage); `policiesLoadState`
                covers the follow-on /leave/policy/resolve call. Both
                render as an actionable banner so operators know exactly
                what needs configuration, instead of staring at an empty
                Leave Type dropdown. */}
            {meLoadState === "unlinked" && (
                <div
                    className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
                    data-testid="apply-unlinked-banner"
                    role="status"
                >
                    <Warning size={18} className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <div className="font-semibold">Your login is not linked to an employee record.</div>
                        <div className="mt-0.5 text-amber-800">
                            Leave applications are attached to an employee profile. Please ask HR to link your account to your employee record — once linked, this page will show your applicable leave types automatically.
                        </div>
                    </div>
                </div>
            )}
            {meLoadState === "error" && (
                <div
                    className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3"
                    data-testid="apply-me-error-banner"
                    role="alert"
                >
                    <Warning size={18} className="text-rose-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-rose-900">
                        <div className="font-semibold">We couldn&apos;t load your employee profile.</div>
                        <div className="mt-0.5 text-rose-800">
                            This is usually a temporary connectivity issue. Please refresh the page or try again in a minute. If the problem persists, contact your HR administrator.
                        </div>
                    </div>
                </div>
            )}
            {meLoadState === "ok" && policiesLoadState === "empty" && (
                <div
                    className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
                    data-testid="apply-no-policies-banner"
                    role="status"
                >
                    <Warning size={18} className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <div className="font-semibold">No leave types are available for your profile yet.</div>
                        <div className="mt-0.5 text-amber-800">
                            An HR administrator needs to assign a leave policy to your role or business unit before you can apply for leave.
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="leave-apply-form">
                    {isHr && (
                        <div>
                            <label className="text-xs text-slate-600">Employee (HR only)</label>
                            <select
                                data-testid="apply-employee"
                                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                value={employeeId}
                                onChange={(e) => setEmployeeId(e.target.value)}
                            >
                                <option value="">{me ? `${me.full_name} (me)` : "Select…"}</option>
                                {employees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <select
                            data-testid="apply-leave-type"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                            value={form.leave_type_code}
                            onChange={(e) => setForm({ ...form, leave_type_code: e.target.value })}
                            disabled={policiesLoadState !== "ok" || policies.length === 0}
                        >
                            {policiesLoadState === "loading" && (
                                <option value="">Loading applicable leave types…</option>
                            )}
                            {policiesLoadState === "empty" && (
                                <option value="">No leave types available — contact HR</option>
                            )}
                            {policiesLoadState === "error" && (
                                <option value="">Failed to load — refresh and try again</option>
                            )}
                            {meLoadState === "unlinked" && (
                                <option value="">Account not linked to an employee record</option>
                            )}
                            {policiesLoadState === "ok" && (
                                <option value="">Select a leave type…</option>
                            )}
                            {policies.map((p) => (
                                <option key={p.code} value={p.code}>
                                    {p.code} · {p.name}{p.is_lop ? " · LOP" : ""}
                                </option>
                            ))}
                        </select>
                        {selectedPolicy && (
                            <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-2 items-center">
                                <Info size={12} />
                                Annual: {selectedPolicy.annual_allocation_days || "—"}d ·
                                Carry-forward: {selectedPolicy.carry_forward_allowed ? `${selectedPolicy.carry_forward_limit_days || "∞"}d` : "no"} ·
                                Notice: {selectedPolicy.min_notice_days || 0}d ·
                                Weekends counted: {selectedPolicy.includes_weekends ? "yes" : "no"}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-600">From Date</label>
                            <Input
                                type="date" data-testid="apply-from-date"
                                value={form.from_date}
                                onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600">To Date</label>
                            <Input
                                type="date" data-testid="apply-to-date"
                                value={form.to_date}
                                onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-600">First-Day Type</label>
                            <select
                                data-testid="apply-first-half"
                                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                value={form.first_half}
                                onChange={(e) => setForm({ ...form, first_half: e.target.value })}
                            >
                                {HALF_DAY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-600">Last-Day Type</label>
                            <select
                                data-testid="apply-last-half"
                                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                value={form.last_half}
                                onChange={(e) => setForm({ ...form, last_half: e.target.value })}
                            >
                                {HALF_DAY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Reason (min 3 chars)</label>
                        <Textarea
                            data-testid="apply-reason"
                            rows={3}
                            placeholder="Why are you applying for this leave?"
                            value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                    <div className="flex gap-2 pt-3 border-t border-slate-100">
                        <Button variant="outline" onClick={() => submit(false)} disabled={submitting || !canSubmit} data-testid="apply-save-draft">
                            <FloppyDisk size={14} className="mr-1" /> Save as Draft
                        </Button>
                        <Button onClick={() => submit(true)} disabled={submitting || !canSubmit || balanceShortfall} data-testid="apply-submit">
                            <PaperPlaneTilt size={14} className="mr-1" /> {submitting ? "Submitting…" : "Submit for Approval"}
                        </Button>
                    </div>
                </div>

                <aside className="space-y-3" data-testid="leave-apply-sidebar">
                    {duration && (
                        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="apply-duration-card">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                                <CalendarPlus size={14} /> Duration Preview
                            </div>
                            <div className="text-3xl font-bold text-slate-900">{duration.days_total}</div>
                            <div className="text-xs text-slate-500 mt-1">working days · {duration.calendar_days} calendar days</div>
                            <div className="mt-3 text-xs space-y-1 max-h-40 overflow-y-auto pr-1">
                                {duration.breakdown.map((d) => (
                                    <div key={d.date} className={`flex justify-between ${d.value === 0 ? "text-slate-400" : "text-slate-700"}`}>
                                        <span>{formatDate(d.date)} · {d.day_of_week}</span>
                                        <span className="font-mono">{d.value === 0 ? "—" : d.value.toFixed(1)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {balance && form.leave_type_code && (
                        <div className={`rounded-xl p-4 border ${balanceShortfall ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="apply-balance-card">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold mb-2">
                                {balanceShortfall ? <Warning size={14} className="text-rose-700" /> : <CheckCircle size={14} className="text-emerald-700" />}
                                <span className={balanceShortfall ? "text-rose-700" : "text-emerald-700"}>Balance Check</span>
                            </div>
                            {/* P0-A Batch 2 · three-value balance display */}
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                    <div className="text-[9px] uppercase text-slate-500">Ledger</div>
                                    <div className="text-lg font-semibold text-slate-800" data-testid="apply-balance-ledger">{Number(balance.current_balance ?? 0).toFixed(2)}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] uppercase text-amber-700">Reserved</div>
                                    <div className="text-lg font-semibold text-amber-900" data-testid="apply-balance-reserved">{Number(balance.reserved_days ?? 0).toFixed(2)}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] uppercase text-emerald-700">Available</div>
                                    <div className="text-lg font-bold text-emerald-900" data-testid="apply-balance-available">{Number(balance.available_balance ?? balance.current_balance ?? 0).toFixed(2)}</div>
                                </div>
                            </div>
                            <div className={`text-xs mt-2 ${balanceShortfall ? "text-rose-800" : "text-emerald-800"}`}>
                                {form.leave_type_code}
                            </div>
                            {balanceShortfall && (
                                <div className="text-xs text-rose-900 mt-2">
                                    Required {duration?.days_total} days. Save as Draft and contact HR, or choose an LOP-eligible policy.
                                </div>
                            )}
                            {selectedPolicy?.is_lop && (
                                <div className="text-xs text-amber-800 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                                    This is a Loss-of-Pay leave type — balance check is bypassed.
                                </div>
                            )}
                        </div>
                    )}

                    {/* P0-A Batch 2 · Provisional Paid/LOP split preview */}
                    {splitPreview && splitPreview.policy_allows_split && Number(splitPreview.days_total || 0) > 0 && (
                        <div className="rounded-xl p-4 border bg-sky-50 border-sky-200" data-testid="apply-split-preview">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-sky-700 font-semibold mb-2">
                                <Info size={14} /> Estimated / Provisional Split
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-center">
                                <div className="bg-white rounded p-2 border border-emerald-200">
                                    <div className="text-[9px] uppercase text-emerald-700">Provisional Paid</div>
                                    <div className="text-lg font-bold text-emerald-900" data-testid="apply-split-paid">{Number(splitPreview.provisional_paid_days || 0).toFixed(2)}d</div>
                                </div>
                                <div className={`rounded p-2 border ${Number(splitPreview.provisional_lop_days || 0) > 0 ? "bg-white border-amber-200" : "bg-white border-slate-200"}`}>
                                    <div className="text-[9px] uppercase text-amber-700">Provisional LOP</div>
                                    <div className={`text-lg font-bold ${Number(splitPreview.provisional_lop_days || 0) > 0 ? "text-amber-900" : "text-slate-400"}`} data-testid="apply-split-lop">{Number(splitPreview.provisional_lop_days || 0).toFixed(2)}d</div>
                                </div>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-2 italic">
                                {splitPreview.note || "Authoritative Paid/LOP split determined at payroll freeze"}
                            </div>
                        </div>
                    )}

                    {conflicts?.has_conflicts && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4" data-testid="apply-conflict-card">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-rose-700 font-semibold mb-2">
                                <Warning size={14} /> Conflicts Detected
                            </div>
                            <div className="space-y-2">
                                {conflicts.conflicts.map((c) => (
                                    <div key={c.id} className="text-xs bg-white border border-rose-100 rounded p-2">
                                        <div className="font-mono text-rose-800">{c.id}</div>
                                        <div className="text-rose-900">
                                            {c.leave_type_code} · {formatDate(c.from_date)} → {formatDate(c.to_date)} · <Badge className="text-[10px]">{c.status}</Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}
